import { makeStyles, mergeClasses } from '@fluentui/react-components';
import { CSSProperties, forwardRef, HTMLAttributes, ReactNode } from 'react';

//
// The layout primitive of the app: a flex column (or row with `horizontal`) with the prop names of the
// Fluent v8 Stack the ISOPlanner client was built on, so a layout reads the same in both code bases.
// Fluent v9 has no Stack; this is plain flexbox. The children gap is the CSS `gap`, so a wrapped row keeps
// its spacing too.
//

export type IPAlignment =
  'start' | 'end' | 'center' | 'space-between' | 'space-around' | 'space-evenly' | 'baseline' | 'stretch';

export interface IPStackTokens {
  childrenGap?: number | string;
  padding?: number | string;
}

export interface IPStackStyles {
  root?: CSSProperties;
}

export interface IPStackProps extends Omit<HTMLAttributes<HTMLDivElement>, 'style' | 'wrap'> {
  horizontal?: boolean;
  wrap?: boolean;
  grow?: boolean | number;
  verticalFill?: boolean;
  horizontalAlign?: IPAlignment;
  verticalAlign?: IPAlignment;
  tokens?: IPStackTokens;
  styles?: IPStackStyles;
  children?: ReactNode;
}

export interface IPStackItemProps extends Omit<HTMLAttributes<HTMLDivElement>, 'style'> {
  grow?: boolean | number;
  shrink?: boolean | number;
  disableShrink?: boolean;
  align?: 'auto' | 'stretch' | 'baseline' | 'start' | 'center' | 'end';
  styles?: IPStackStyles;
  children?: ReactNode;
}

const useStackStyles = makeStyles({
  root: {
    display: 'flex',
    flexWrap: 'nowrap',
    boxSizing: 'border-box',
  },
  vertical: {
    flexDirection: 'column',
  },
  horizontal: {
    flexDirection: 'row',
  },
  wrap: {
    flexWrap: 'wrap',
  },
  verticalFill: {
    height: '100%',
  },
});

const toFlexAlignment = (alignment: IPAlignment | undefined): string | undefined => {
  switch (alignment) {
    case 'start':
      return 'flex-start';
    case 'end':
      return 'flex-end';
    default:
      return alignment;
  }
};

const toCssSize = (value: number | string | undefined): string | undefined =>
  typeof value === 'number' ? `${value}px` : value;

const toGrow = (grow: boolean | number | undefined): number | undefined =>
  grow === true ? 1 : typeof grow === 'number' ? grow : undefined;

const StackRoot = forwardRef<HTMLDivElement, IPStackProps>((props, ref) => {
  const {
    horizontal,
    wrap,
    grow,
    verticalFill,
    horizontalAlign,
    verticalAlign,
    tokens,
    styles,
    className,
    children,
    ...rest
  } = props;
  const classes = useStackStyles();

  //in a column the horizontal alignment is the cross axis, in a row it is the main axis
  const mainAxis = toFlexAlignment(horizontal ? horizontalAlign : verticalAlign);
  const crossAxis = toFlexAlignment(horizontal ? verticalAlign : horizontalAlign);

  const style: CSSProperties = {
    justifyContent: mainAxis,
    alignItems: crossAxis,
    gap: toCssSize(tokens?.childrenGap),
    padding: toCssSize(tokens?.padding),
    flexGrow: toGrow(grow),
    ...styles?.root,
  };

  return (
    <div
      ref={ref}
      className={mergeClasses(
        classes.root,
        horizontal ? classes.horizontal : classes.vertical,
        wrap && classes.wrap,
        verticalFill && classes.verticalFill,
        className,
      )}
      style={style}
      {...rest}
    >
      {children}
    </div>
  );
});

StackRoot.displayName = 'IPStack';

const StackItem = forwardRef<HTMLDivElement, IPStackItemProps>((props, ref) => {
  const { grow, shrink, disableShrink, align, styles, className, children, ...rest } = props;

  //as in the v8 Stack: an item neither growing nor asked to shrink keeps its size
  let flexShrink: number | undefined = undefined;
  if (disableShrink || (!grow && !shrink)) {
    flexShrink = 0;
  } else if (shrink) {
    flexShrink = shrink === true ? 1 : shrink;
  }

  const style: CSSProperties = {
    flexGrow: toGrow(grow),
    flexShrink,
    alignSelf: align === 'start' ? 'flex-start' : align === 'end' ? 'flex-end' : align,
    ...styles?.root,
  };

  return (
    <div ref={ref} className={className} style={style} {...rest}>
      {children}
    </div>
  );
});

StackItem.displayName = 'IPStack.Item';

type IPStackComponent = typeof StackRoot & { Item: typeof StackItem };

export const IPStack = StackRoot as IPStackComponent;
IPStack.Item = StackItem;
